import { cn } from '@/lib/utils'
import type { RangeMeterBand, RangeMeterBandLabel } from './types'

export interface RangeMeterProps {
  label: string
  value: number
  max: number
  band: RangeMeterBand
  bandLabel?: RangeMeterBandLabel
  className?: string
}

const bandClasses: Record<RangeMeterBand, string> = {
  red: 'bg-destructive',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
}

const defaultBandLabels: Record<RangeMeterBand, RangeMeterBandLabel> = {
  red: 'High',
  amber: 'Elevated',
  green: 'Normal',
}

function clampRatio(value: number, max: number) {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0
  return Math.max(0, Math.min(1, value / max))
}

export function RangeMeter({
  label,
  value,
  max,
  band,
  bandLabel,
  className,
}: RangeMeterProps) {
  const ratio = clampRatio(value, max)
  const ratioText = `${value} / ${max}`
  const percentText = `${Math.round(ratio * 100)}%`
  const visibleBandLabel = bandLabel ?? defaultBandLabels[band]

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          {ratioText} ({percentText})
        </span>
      </div>
      <div
        className="h-3 rounded-full bg-muted"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={Math.max(0, Math.min(value, max))}
        aria-valuetext={`${ratioText}, ${percentText}, ${visibleBandLabel}`}
      >
        <div
          className={cn('h-full rounded-full', bandClasses[band])}
          style={{ width: percentText }}
        />
      </div>
      <p className="text-xs font-medium text-muted-foreground">
        Band: {visibleBandLabel}. Ratio: {ratioText}.
      </p>
    </div>
  )
}
